<template>
  <div class="teacher-status max-w-7xl mx-auto space-y-6">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl text-gray-900">教师状态维护</h1>
        <p class="text-sm text-gray-500 mt-1">管理教师信息、处理信息变更申请</p>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="capsule-tabs">
      <el-tab-pane name="list">
        <template #label>
          <div class="tab-chip">
            <el-icon class="mr-2"><User /></el-icon>
            教师列表
          </div>
        </template>
      </el-tab-pane>
      <el-tab-pane name="reports">
        <template #label>
          <div class="tab-chip">
            <el-icon class="mr-2"><Warning /></el-icon>
            信息变更申请
            <span v-if="pendingReports.length" class="badge-red">{{ pendingReports.length }}</span>
          </div>
        </template>
      </el-tab-pane>
    </el-tabs>

    <div v-if="activeTab === 'list'" class="space-y-4">
      <div v-if="canViewTeacherStatus" class="card p-6">
        <div class="flex flex-col space-y-4">
          <!-- 搜索框 -->
          <div class="relative">
            <el-input
              v-model="searchTerm"
              size="large"
              placeholder="搜索教师姓名或工号..."
              clearable
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
          </div>
          
          <!-- 筛选器 -->
          <div class="flex flex-wrap items-center gap-4">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-600 whitespace-nowrap">状态：</span>
              <el-select v-model="selectedStatus" size="large" class="w-32">
                <el-option label="全部" value="全部" />
                <el-option label="在职" value="在职" />
                <el-option label="休假" value="休假" />
                <el-option label="离职" value="离职" />
              </el-select>
            </div>
            
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-600 whitespace-nowrap">教研室：</span>
              <el-select v-model="selectedDepartment" size="large" class="w-40" filterable>
                <el-option label="全部" value="全部" />
                <el-option v-for="dept in departmentOptions" :key="dept" :label="dept" :value="dept" />
              </el-select>
            </div>

            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-600 whitespace-nowrap">排序：</span>
              <el-select v-model="sortField" size="large" class="w-28">
                <el-option label="入职时间" value="entry" />
                <el-option label="姓名" value="name" />
                <el-option label="职称" value="title" />
                <el-option label="状态" value="status" />
              </el-select>
              <el-select v-model="sortOrder" size="large" class="w-24">
                <el-option label="降序" value="desc" />
                <el-option label="升序" value="asc" />
              </el-select>
            </div>

            <div class="ml-auto text-sm text-gray-600">
              共 {{ filteredTeachers.length }} 名教师
            </div>
          </div>
        </div>
      </div>
      <div v-else class="card p-6 text-center">
        <h3 class="text-gray-900 mb-2">当前未开放时间段</h3>
        <p class="text-gray-600 text-sm">请联系管理员或在开放期间再来查看教师状态</p>
      </div>

      <div v-if="canViewTeacherStatus" class="space-y-3">
        <div
          v-for="teacher in currentTeachers"
          :key="teacher.id"
          class="card teacher-card"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center space-x-3 mb-2">
                <h3 class="text-gray-900">{{ teacher.name }}</h3>
                <span class="status-chip" :class="statusClass(teacher.status)">{{ teacher.status }}</span>
                <span class="title-chip">{{ teacher.title }}</span>
              </div>
              <div class="info-line text-sm text-gray-600">
                <span>工号：{{ teacher.number }}</span>
                <span>教研室：{{ teacher.department }}</span>
                <span>入职时间：{{ formatDate(teacher.hireDate) }}</span>
                <span>工龄：{{ calculateYearsOfService(teacher.hireDate) }} 年</span>
              </div>
            </div>
            <div class="flex gap-2 ml-4">
              <button v-if="canViewTeacherStatus" class="outline-btn" @click="openViewDialog(teacher)">
                <el-icon class="mr-1"><View /></el-icon>
                查看
              </button>
              <button v-if="canModifyTeacherStatus" class="outline-btn" @click="openEditDialog(teacher)">
                <el-icon class="mr-1"><EditPen /></el-icon>
                编辑
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="totalTeacherPages > 1" class="mt-6 flex justify-center">
        <nav role="navigation" aria-label="teacher-pagination" class="dm-pagination mx-auto flex w-full justify-center">
          <ul class="flex flex-row items-center gap-1">
            <!-- Previous -->
            <li>
              <button
                @click="goToTeacherPrevious"
                :disabled="currentTeacherPage === 1"
                class="inline-flex items-center justify-center gap-1 h-9 rounded-md text-sm font-medium transition-all px-2.5 sm:pl-2.5 disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <ChevronLeft class="size-4" />
                <span class="hidden sm:block">Previous</span>
              </button>
            </li>

            <!-- 中间页码与省略号 -->
            <template v-for="pageNum in Array.from({length: totalTeacherPages}, (_, i) => i + 1)" :key="pageNum">
              <!-- 显示：第1页、最后一页、当前页及其前后各一页 -->
              <template v-if="pageNum === 1 || pageNum === totalTeacherPages || (pageNum >= currentTeacherPage - 1 && pageNum <= currentTeacherPage + 1)">
                <li>
                  <button
                    @click="goToTeacherPage(pageNum)"
                    :aria-current="currentTeacherPage === pageNum ? 'page' : undefined"
                    :class="[
                      'inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                      currentTeacherPage === pageNum ? 'border bg-white text-gray-900' : 'text-gray-900'
                    ]"
                  >
                    {{ pageNum }}
                  </button>
                </li>
              </template>
              <template v-else-if="pageNum === currentTeacherPage - 2 || pageNum === currentTeacherPage + 2">
                <li>
                  <span class="flex w-9 h-9 items-center justify-center">
                    <MoreHorizontal class="size-4" />
                    <span class="sr-only">More pages</span>
                  </span>
                </li>
              </template>
            </template>

            <!-- Next -->
            <li>
              <button
                @click="goToTeacherNext"
                :disabled="currentTeacherPage === totalTeacherPages"
                class="inline-flex items-center justify-center gap-1 h-9 rounded-md text-sm font-medium transition-all px-2.5 sm:pr-2.5 disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <span class="hidden sm:block">Next</span>
                <ChevronRight class="size-4" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div v-else class="space-y-4">
      <div class="card p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-gray-900 text-lg">待处理的信息变更申请</h3>
          <span class="text-sm text-gray-500">共 {{ pendingReports.length }} 条</span>
        </div>
        <div v-if="!currentPendingReports.length" class="py-12 text-center space-y-2 text-gray-500 text-sm">
          <p>暂无待处理申请</p>
          <p>所有信息变更已完成处理</p>
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="report in currentPendingReports"
            :key="report.id"
            class="card report-card"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2 mb-1">
                  <h4 class="text-gray-900">{{ report.teacher.name }}</h4>
                  <span class="text-sm text-gray-500">工号：{{ report.teacher.number }}</span>
                  <span class="status-chip pending">待处理</span>
                </div>
                <div class="text-sm text-gray-600 space-y-1 break-words whitespace-normal">
                  <div>
                    有误字段：
                    <span class="break-words">{{ report.changes.map((c) => c.fieldLabel).join('、') }}</span>
                  </div>
                  <div v-if="report.description">
                    教师反馈：
                    <span class="break-words">{{ report.description }}</span>
                  </div>
                  <div class="text-sm text-gray-600">提交时间：{{ formatDateTime(report.submitTime) }}</div>
                </div>
              </div>
              <button v-if="canModifyTeacherStatus" class="primary-btn h-9 px-6 inline-flex items-center shrink-0" @click="openHandleDialog(report)">
                <el-icon class="mr-1"><Edit /></el-icon>
                处理
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="totalReportPages > 1" class="flex justify-center">
        <nav role="navigation" aria-label="report-pagination" class="pagination">
          <button @click="goToReportPage(currentReportPage - 1)" :disabled="currentReportPage === 1">
            上一页
          </button>
          <button
            v-for="page in reportPageNumbers"
            :key="page"
            :class="{ active: page === currentReportPage }"
            @click="goToReportPage(page)"
          >
            {{ page }}
          </button>
          <button
            @click="goToReportPage(currentReportPage + 1)"
            :disabled="currentReportPage === totalReportPages"
          >
            下一页
          </button>
        </nav>
      </div>

      <div class="card p-6">
        <h3 class="text-gray-900 text-lg mb-4">处理历史</h3>
        <div class="space-y-3">
          <div
            v-for="history in historyReports"
            :key="history.id"
            class="history-row"
          >
            <div>
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm text-gray-900 font-medium">{{ history.teacher.name }}</span>
                <span class="status-chip" :class="history.status === 'processed' ? 'processed' : 'ignored'">{{ history.status === 'processed' ? '已处理' : '已忽略' }}</span>
              </div>
              <div class="text-xs text-gray-500">
                处理时间：{{ formatDateTime(history.handle_time || history.submitTime) }}
                <span class="ml-3 text-xs text-gray-500">处理人：{{ history.handler || '管理员' }}</span>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>

    <!-- 查看 -->
    <el-dialog
      v-model="viewDialogOpen"
      title="教师详细信息"
      width="480px"
      :destroy-on-close="true"
    >
      <div v-if="selectedTeacher" class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div>
          <div class="text-sm text-gray-600 mb-2">工号</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.number }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">姓名</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.name }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">性别</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.gender || '-' }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">出生日期</div>
          <div class="text-base text-gray-900">{{ formatDate(selectedTeacher.birthDate) }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">职称</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.title }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">当前状态</div>
          <div>
            <el-tag size="small" :type="statusTagType(selectedTeacher.status)">{{ selectedTeacher.status }}</el-tag>
          </div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">入职时间</div>
          <div class="text-base text-gray-900">{{ formatDate(selectedTeacher.hireDate) }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">工龄</div>
          <div class="text-base text-gray-900">{{ calculateYearsOfService(selectedTeacher.hireDate) }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">最高学历</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.education || '-' }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">最高学位</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.degree || '-' }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">所属学院</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.college }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">所属教研室</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.department }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">联系电话</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.phone || '-' }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">电子邮箱</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.email || '-' }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 mb-2">角色</div>
          <div class="text-base text-gray-900">{{ selectedTeacher.role || '普通教师' }}</div>
        </div>
      </div>
    </el-dialog>

    <!-- 编辑 -->
    <el-dialog
      v-model="editDialogOpen"
      title="编辑教师信息"
      width="480px"
      :destroy-on-close="true"
    >
      <template #header>
        <div>
          <div class="text-lg font-semibold text-gray-900">编辑教师信息</div>
          <p class="text-sm text-gray-600 mt-1">修改教师的基本信息和状态</p>
        </div>
      </template>
      <div v-if="selectedTeacher" class="grid grid-cols-2 gap-x-3 gap-y-3">
        <div>
          <label class="block mb-2 text-gray-900">工号</label>
          <el-input v-model="editTeacherForm.number" size="large" disabled class="bg-gray-50" />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">姓名</label>
          <el-input v-model="editTeacherForm.name" size="large" />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">性别</label>
          <el-select v-model="editTeacherForm.gender" class="w-full" size="large">
            <el-option label="男" value="男" />
            <el-option label="女" value="女" />
            <el-option label="其他" value="其他" />
          </el-select>
        </div>
        <div>
          <label class="block mb-2 text-gray-900">出生日期</label>
          <el-date-picker
            v-model="editTeacherForm.birthDate"
            type="date"
            size="large"
            value-format="YYYY-MM-DD"
            placeholder="选择日期"
            class="w-full"
          />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">职称</label>
          <el-select v-model="editTeacherForm.title" class="w-full" size="large">
            <el-option label="助教" value="助教" />
            <el-option label="讲师" value="讲师" />
            <el-option label="副教授" value="副教授" />
            <el-option label="教授" value="教授" />
          </el-select>
        </div>
        <div>
          <label class="block mb-2 text-gray-900">当前状态</label>
          <el-select v-model="editTeacherForm.status" class="w-full" size="large">
            <el-option label="在职" value="在职" />
            <el-option label="休假" value="休假" />
            <el-option label="离职" value="离职" />
          </el-select>
        </div>
        <div>
          <label class="block mb-2 text-gray-900">入职时间</label>
          <el-date-picker
            v-model="editTeacherForm.hireDate"
            type="date"
            size="large"
            value-format="YYYY-MM-DD"
            placeholder="选择日期"
            class="w-full"
          />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">最高学历</label>
          <el-select v-model="editTeacherForm.education" class="w-full" size="large">
            <el-option label="本科" value="本科" />
            <el-option label="硕士研究生" value="硕士研究生" />
            <el-option label="博士研究生" value="博士研究生" />
          </el-select>
        </div>
        <div>
          <label class="block mb-2 text-gray-900">最高学位</label>
          <el-select v-model="editTeacherForm.degree" class="w-full" size="large">
            <el-option label="学士" value="学士" />
            <el-option label="硕士" value="硕士" />
            <el-option label="博士" value="博士" />
          </el-select>
        </div>
        <div>
          <label class="block mb-2 text-gray-900">所属学院</label>
          <el-input v-model="editTeacherForm.college" size="large" />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">所属教研室</label>
          <el-select v-model="editTeacherForm.department" class="w-full" size="large">
            <el-option label="计算机教研室" value="计算机教研室" />
            <el-option label="软件工程教研室" value="软件工程教研室" />
            <el-option label="网络工程教研室" value="网络工程教研室" />
            <el-option label="人工智能教研室" value="人工智能教研室" />
          </el-select>
        </div>
        <div>
          <label class="block mb-2 text-gray-900">联系电话</label>
          <el-input v-model="editTeacherForm.phone" size="large" />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">电子邮箱</label>
          <el-input v-model="editTeacherForm.email" type="email" size="large" />
        </div>
        <div>
          <label class="block mb-2 text-gray-900">角色</label>
          <el-select v-model="editTeacherForm.role" class="w-full" size="large">
            <el-option label="普通教师" value="普通教师" />
            <el-option label="系统管理员" value="系统管理员" />
          </el-select>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <el-button @click="editDialogOpen = false">取消</el-button>
          <el-button type="primary" @click="saveEditTeacher">保存修改</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 处理 -->
            <el-dialog
      v-model="handleDialogOpen"
      width="640px"
      :destroy-on-close="true"
      class="teacher-dialog"
    >
      <template #header>
        <div>
          <div class="text-lg font-semibold text-gray-900">处理信息变更申请</div>
          <p class="text-sm text-gray-600 mt-1">修改有误的字段信息</p>
        </div>
      </template>
      <div v-if="selectedReport" class="space-y-2 px-1">
        <div v-if="selectedReport.description" class="feedback-card">
          <div class="mb-2">
            <span class="label">教师反馈：</span>
            <span>{{ selectedReport.description }}</span>
          </div>
          <div>
            <span class="label">有误字段：</span>
            <span>{{ selectedReport.changes.map((c) => c.fieldLabel).join('、') }}</span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-x-3 gap-y-3">
          <div>
            <div class="text-sm text-gray-600 mb-2">
              工号
            <span v-if="isFieldEditable('number')" class="text-red-500 ml-1">*</span>
            </div>
            <el-input v-if="isFieldEditable('number')" v-model="handleFormData.number" size="large" />
            <div v-else class="text-base text-gray-900">{{ handleFormData.number }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              姓名
            <span v-if="isFieldEditable('name')" class="text-red-500 ml-1">*</span>
            </div>
            <el-input v-if="isFieldEditable('name')" v-model="handleFormData.name" size="large" />
            <div v-else class="text-base text-gray-900">{{ handleFormData.name }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              性别
              <span v-if="isFieldEditable('gender')" class="text-red-500 ml-1">*</span>
            </div>
            <el-select v-if="isFieldEditable('gender')" v-model="handleFormData.gender" class="w-full" size="large">
              <el-option label="男" value="男" />
              <el-option label="女" value="女" />
              <el-option label="其他" value="其他" />
            </el-select>
            <div v-else class="text-base text-gray-900">{{ handleFormData.gender || '-' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              出生日期
              <span v-if="isFieldEditable('birthDate')" class="text-red-500 ml-1">*</span>
            </div>
            <el-date-picker
              v-if="isFieldEditable('birthDate')"
              v-model="handleFormData.birthDate"
              type="date"
              size="large"
              value-format="YYYY-MM-DD"
              placeholder="选择日期"
              class="w-full"
              style="width: 100%"
            />
            <div v-else class="text-base text-gray-900">{{ formatDate(handleFormData.birthDate) }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              职称
              <span v-if="isFieldEditable('title')" class="text-red-500 ml-1">*</span>
            </div>
            <el-select v-if="isFieldEditable('title')" v-model="handleFormData.title" class="w-full" size="large">
              <el-option label="助教" value="助教" />
              <el-option label="讲师" value="讲师" />
              <el-option label="副教授" value="副教授" />
              <el-option label="教授" value="教授" />
            </el-select>
            <div v-else class="text-base text-gray-900">{{ handleFormData.title }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              当前状态
              <span v-if="isFieldEditable('status')" class="text-red-500 ml-1">*</span>
            </div>
            <el-select v-if="isFieldEditable('status')" v-model="handleFormData.status" class="w-full" size="large">
              <el-option label="在职" value="在职" />
              <el-option label="休假" value="休假" />
              <el-option label="离职" value="离职" />
            </el-select>
            <div v-else class="text-base text-gray-900">{{ handleFormData.status }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              入职时间
              <span v-if="isFieldEditable('hireDate')" class="text-red-500 ml-1">*</span>
            </div>
            <el-date-picker
              v-if="isFieldEditable('hireDate')"
              v-model="handleFormData.hireDate"
              type="date"
              size="large"
              value-format="YYYY-MM-DD"
              placeholder="选择日期"
              class="w-full"
              style="width: 100%"
            />
            <div v-else class="text-base text-gray-900">{{ formatDate(handleFormData.hireDate) }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              最高学历
              <span v-if="isFieldEditable('education')" class="text-red-500 ml-1">*</span>
            </div>
            <el-select v-if="isFieldEditable('education')" v-model="handleFormData.education" class="w-full" size="large">
              <el-option label="本科" value="本科" />
              <el-option label="硕士研究生" value="硕士研究生" />
              <el-option label="博士研究生" value="博士研究生" />
            </el-select>
            <div v-else class="text-base text-gray-900">{{ handleFormData.education || '-' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              最高学位
              <span v-if="isFieldEditable('degree')" class="text-red-500 ml-1">*</span>
            </div>
            <el-select v-if="isFieldEditable('degree')" v-model="handleFormData.degree" class="w-full" size="large">
              <el-option label="学士" value="学士" />
              <el-option label="硕士" value="硕士" />
              <el-option label="博士" value="博士" />
            </el-select>
            <div v-else class="text-base text-gray-900">{{ handleFormData.degree || '-' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              所属学院
              <span v-if="isFieldEditable('college')" class="text-red-500 ml-1">*</span>
            </div>
            <el-input v-if="isFieldEditable('college')" v-model="handleFormData.college" size="large" />
            <div v-else class="text-base text-gray-900">{{ handleFormData.college }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              所属教研室
              <span v-if="isFieldEditable('department')" class="text-red-500 ml-1">*</span>
            </div>
            <el-input v-if="isFieldEditable('department')" v-model="handleFormData.department" size="large" />
            <div v-else class="text-base text-gray-900">{{ handleFormData.department }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              角色
              <span v-if="isFieldEditable('role')" class="text-red-500 ml-1">*</span>
            </div>
            <el-select v-if="isFieldEditable('role')" v-model="handleFormData.role" class="w-full" size="large">
              <el-option label="普通教师" value="普通教师" />
              <el-option label="系统管理员" value="系统管理员" />
            </el-select>
            <div v-else class="text-base text-gray-900">{{ handleFormData.role || '普通教师' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              联系电话
              <span v-if="isFieldEditable('phone')" class="text-red-500 ml-1">*</span>
            </div>
            <el-input v-if="isFieldEditable('phone')" v-model="handleFormData.phone" size="large" />
            <div v-else class="text-base text-gray-900">{{ handleFormData.phone || '-' }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600 mb-2">
              电子邮箱
              <span v-if="isFieldEditable('email')" class="text-red-500 ml-1">*</span>
            </div>
            <el-input v-if="isFieldEditable('email')" v-model="handleFormData.email" type="email" size="large" />
            <div v-else class="text-base text-gray-900">{{ handleFormData.email || '-' }}</div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <el-button size="large" @click="handleDialogOpen = false">取消</el-button>
          <el-button size="large" @click="submitIgnoreResult">忽略</el-button>
          <el-button size="large" class="success-button" @click="submitHandleResult">确认处理</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useAppStore } from '@/stores'
import { ElMessage } from 'element-plus'
import { Search, Warning, User, View, EditPen, Edit } from '@element-plus/icons-vue'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-vue-next'
import { getTeacherList, getTeacherById, updateTeacherInfo, getFeedbackList, updateFeedbackStatus } from '@/api/teacherStatus'

const activeTab = ref('list')
const loading = ref(false)
const store = useAppStore()
const canViewTeacherStatus = computed(() => store.hasFunctionByName('查看教师状态'))
const canModifyTeacherStatus = computed(() => store.hasFunctionByName('修改教师状态'))

const teachers = ref([])
// 替换为后端数据：初始化为空列表，并记录后端总数
const teacherTotal = ref(0)

const searchTerm = ref('')
const selectedStatus = ref('全部')
const selectedDepartment = ref('全部')
const sortField = ref('entry')
const sortOrder = ref('desc')
const itemsPerPage = 5
const currentTeacherPage = ref(1)

watch([searchTerm, selectedStatus, selectedDepartment, sortField, sortOrder], () => {
  currentTeacherPage.value = 1
  fetchTeachers()
})

const departmentOptions = computed(() =>
  Array.from(new Set(teachers.value.map((t) => t.department)))
)

const filteredTeachers = computed(() => teachers.value)
const sortedTeachers = computed(() => {
  const arr = Array.isArray(teachers.value) ? [...teachers.value] : []
  const field = sortField.value
  const dir = sortOrder.value === 'asc' ? 1 : -1
  const titleRank = (v) => {
    const m = { '助教': 1, '讲师': 2, '副教授': 3, '教授': 4 }
    const s = String(v || '').trim()
    return m[s] ?? 0
  }
  const statusRank = (v) => {
    const m = { '在职': 1, '休假': 2, '离职': 3 }
    const s = String(v || '').trim()
    return m[s] ?? 0
  }
  const cmp = (a, b) => {
    if (field === 'entry') {
      const ta = a.hireDate ? new Date(a.hireDate).getTime() : 0
      const tb = b.hireDate ? new Date(b.hireDate).getTime() : 0
      return (ta - tb) * dir
    }
    if (field === 'name') {
      const sa = String(a.name || '')
      const sb = String(b.name || '')
      return sa.localeCompare(sb, 'zh-Hans') * dir
    }
    if (field === 'title') {
      const ra = titleRank(a.title)
      const rb = titleRank(b.title)
      if (ra !== rb) return (ra - rb) * dir
      const sa = String(a.title || '')
      const sb = String(b.title || '')
      return sa.localeCompare(sb, 'zh-Hans') * dir
    }
    if (field === 'status') {
      const ra = statusRank(a.status)
      const rb = statusRank(b.status)
      if (ra !== rb) return (ra - rb) * dir
      const sa = String(a.status || '')
      const sb = String(b.status || '')
      return sa.localeCompare(sb, 'zh-Hans') * dir
    }
    return 0
  }
  return arr.sort(cmp)
})

const totalTeacherPages = computed(() => Math.max(1, Math.ceil((teacherTotal.value || filteredTeachers.value.length) / itemsPerPage)))
const currentTeachers = computed(() => sortedTeachers.value)

function goToTeacherPage(page) {
  if (page < 1 || page > totalTeacherPages.value) return
  currentTeacherPage.value = page
  fetchTeachers()
}

function goToTeacherPrevious() {
  currentTeacherPage.value = Math.max(1, currentTeacherPage.value - 1)
  fetchTeachers()
}

function goToTeacherNext() {
  currentTeacherPage.value = Math.min(totalTeacherPages.value, currentTeacherPage.value + 1)
  fetchTeachers()
}

const reports = ref([])

const pendingReports = computed(() => reports.value.filter((report) => report.status === 'pending'))
const historyReports = computed(() => reports.value.filter((report) => report.status !== 'pending'))

const currentReportPage = ref(1)
const reportItemsPerPage = 4

const totalReportPages = computed(() =>
  Math.max(1, Math.ceil(pendingReports.value.length / reportItemsPerPage))
)
const currentPendingReports = computed(() => {
  const start = (currentReportPage.value - 1) * reportItemsPerPage
  return pendingReports.value.slice(start, start + reportItemsPerPage)
})

const reportPageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalReportPages.value; i += 1) pages.push(i)
  return pages
})

function goToReportPage(page) {
  if (page < 1 || page > totalReportPages.value) return
  currentReportPage.value = page
}

const viewDialogOpen = ref(false)
const editDialogOpen = ref(false)
const handleDialogOpen = ref(false)
const selectedTeacher = ref(null)

const createBlankTeacher = () => ({
  id: null,
  number: '',
  name: '',
  gender: '',
  birthDate: '',
  title: '',
  status: '',
  department: '',
  college: '信息科学与工程学院',
  education: '',
  degree: '',
  hireDate: '',
  phone: '',
  email: '',
  role: '普通教师',
  remark: ''
})

const editTeacherForm = ref(createBlankTeacher())
const selectedReport = ref(null)
const handleFormData = ref(createBlankTeacher())
const handleEditableFields = ref([])
const handleNote = ref('')

const isFieldEditable = (field) => handleEditableFields.value.includes(field)

function fillForm(target, source = {}) {
  Object.assign(target, {
    id: source.id ?? null,
    number: source.number || source.teacher_id || '',
    name: source.name || source.teacher_name || '',
    gender: toGenderLabel(source.gender ?? source.sex ?? source.genderCode),
    birthDate: formatDateForInput(source.birthDate || source.birth_date || ''),
    title: source.title || source.teacher_title || '',
    status: source.status || source.t_status || '在职',
    department: source.department || '',
    college: source.college || '信息科学与工程学院',
    education: toEducationLabel(source.highestEducation ?? source.educationLevel ?? source.education ?? source.educationBackground),
    degree: toDegreeLabel(source.highestDegree ?? source.degreeName ?? source.degree),
    hireDate: formatDateForInput(source.hireDate || source.entry_time || ''),
    phone: source.phone || '',
    email: source.email || '',
    role: source.role || '普通教师',
    remark: source.remark || ''
  })
}

function toGenderLabel(val) {
  const v = typeof val === 'string' ? val.trim().toLowerCase() : val
  if (v === '男' || v === 'male' || v === 'm' || v === 1 || v === '1') return '男'
  if (v === '女' || v === 'female' || v === 'f' || v === 2 || v === '2') return '女'
  if (v === '其他' || v === 'other' || v === 3 || v === '3') return '其他'
  return typeof val === 'string' ? val : ''
}

function toEducationLabel(val) {
  const v = typeof val === 'string' ? val.trim().toLowerCase() : val
  if (v === '本科' || v === 'undergraduate' || v === 'bachelor' || v === 1 || v === '1') return '本科'
  if (v === '硕士研究生' || v === '硕士' || v === 'master' || v === 2 || v === '2') return '硕士研究生'
  if (v === '博士研究生' || v === '博士' || v === 'doctor' || v === 3 || v === '3') return '博士研究生'
  return typeof val === 'string' ? val : ''
}

function toDegreeLabel(val) {
  const v = typeof val === 'string' ? val.trim().toLowerCase() : val
  if (v === '学士' || v === 'bachelor' || v === 1 || v === '1') return '学士'
  if (v === '硕士' || v === 'master' || v === 2 || v === '2') return '硕士'
  if (v === '博士' || v === 'doctor' || v === 3 || v === '3') return '博士'
  return typeof val === 'string' ? val : ''
}

function toStatusLabel(code) {
  if (code === null || code === undefined || code === '') return ''
  const n = Number(code)
  const map = { 1: '在职', 2: '休假', 3: '离职' }
  return map[n] || ''
}

function toGenderCode(val) {
  const v = typeof val === 'string' ? val.trim().toLowerCase() : val
  if (v === '男' || v === 'male' || v === 'm' || v === 1 || v === '1') return 1
  if (v === '女' || v === 'female' || v === 'f' || v === 2 || v === '2') return 2
  if (v === '其他' || v === 'other' || v === 3 || v === '3') return 3
  return 0
}

function toStatusCode(val) {
  const v = typeof val === 'string' ? val.trim() : val
  if (v === '在职' || v === 1 || v === '1') return 1
  if (v === '休假' || v === 2 || v === '2') return 2
  if (v === '离职' || v === 3 || v === '3') return 3
  return undefined
}

function normalizeDate(val) {
  if (!val) return ''
  const s = String(val).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(s)) return s.replace(/\//g, '-')
  try {
    const d = new Date(s)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
  } catch {
    return s
  }
}

function mapTeacherToApi(form) {
  return {
    id: form.id,
    employeeNumber: form.number,
    name: form.name,
    gender: toGenderCode(form.gender),
    birthDate: normalizeDate(form.birthDate),
    professionalTitle: form.title,
    currentStatus: toStatusCode(form.status),
    employmentDate: normalizeDate(form.hireDate),
    highestEducation: form.education,
    highestDegree: form.degree,
    college: form.college,
    department: form.department,
    phone: form.phone,
    email: form.email,
    role: form.role,
    remark: form.remark
  }
}

function buildPartialUpdatePayload(form, editableKeys = []) {
  const full = mapTeacherToApi(form)
  const fieldMap = {
    number: 'employeeNumber',
    name: 'name',
    gender: 'gender',
    birthDate: 'birthDate',
    title: 'professionalTitle',
    status: 'currentStatus',
    hireDate: 'employmentDate',
    education: 'highestEducation',
    degree: 'highestDegree',
    college: 'college',
    department: 'department',
    phone: 'phone',
    email: 'email',
    role: 'role',
    remark: 'remark'
  }
  const payload = { id: full.id }
  editableKeys.forEach((k) => {
    const apiKey = fieldMap[k]
    if (apiKey && full[apiKey] !== undefined && full[apiKey] !== null) {
      payload[apiKey] = full[apiKey]
    }
  })
  return payload
}

// ========== API调用函数 ==========

// 加载教师列表
async function fetchTeachers() {
  try {
    loading.value = true
    
    const params = {
      page: currentTeacherPage.value,
      pageSize: itemsPerPage,
      name: searchTerm.value || undefined,
      currentStatus: selectedStatus.value !== '全部' ? (selectedStatus.value === '在职' ? 1 : (selectedStatus.value === '休假' ? 2 : (selectedStatus.value === '离职' ? 3 : undefined))) : undefined,
      department: selectedDepartment.value !== '全部' ? selectedDepartment.value : undefined,
      sortField: (() => {
        switch (sortField.value) {
          case 'entry':
            return 'employmentDate'
          case 'title':
            return 'professionalTitle'
          case 'status':
            return 'currentStatus'
          case 'name':
          default:
            return 'name'
        }
      })(),
      sortOrder: sortOrder.value
    }
    
    const res = await getTeacherList(params)
    const pageData = res?.data ?? res
    const records = pageData?.records || []
    teacherTotal.value = pageData?.total ?? records.length
    
    // 映射后端字段到前端字段
    teachers.value = records.map(t => ({
      id: t.id,
      number: t.employeeNumber || t.number || '',
      name: t.name || '',
      gender: toGenderLabel(t.genderName ?? t.gender ?? t.genderCode ?? t.sex),
      birthDate: t.birthDate || '',
      phone: t.phone || '',
      email: t.email || '',
      hireDate: t.employmentDate || t.entryTime || t.hireDate || '',
      education: toEducationLabel(t.highestEducation ?? t.educationLevel ?? t.education ?? t.educationBackground),
      degree: toDegreeLabel(t.highestDegree ?? t.degreeName ?? t.degree),
      college: t.college || '信息科学与工程学院',
      department: t.department || '',
      title: t.professionalTitle || t.title || '',
      status: t.statusName || toStatusLabel(t.currentStatus) || t.status || '',
      role: t.role || '普通教师',
      remark: t.remark || ''
    }))
    
  } catch (error) {
    console.error('加载教师列表失败:', error)
    ElMessage.error('加载教师列表失败')
  } finally {
    loading.value = false
  }
}

// 加载信息反馈列表
async function fetchFeedbacks() {
  try {
    loading.value = true
    
    const params = {
      page: 1,
      pageSize: 100  // 加载所有反馈
    }
    
    const res = await getFeedbackList(params)
    const pageData = res?.data ?? res
    const records = pageData?.records || []
    
    // 转换后端数据格式为前端格式
    reports.value = records.map(r => ({
      id: r.id,
      teacher: {
        id: r.userId,
        name: r.teacherName || '未知',
        number: r.employeeNumber || ''
      },
      description: r.description || '',
      submitTime: r.createTime || r.submitTime,
      status: (() => {
        const s = r.processStatus
        if (s === 1 || s === '1') return 'pending'
        if (s === 2 || s === '2') return 'processed'
        if (s === 3 || s === '3') return 'ignored'
        return 'pending'
      })(),
      changes: parseChanges(r.errorFields),
      handler: r.processedBy || '管理员',
      handle_time: r.updateTime || r.update_time || r.processTime || ''
    }))
    
    try {
      const store = useAppStore()
      const pending = reports.value.filter(r => r.status === 'pending').length
      store.setPendingFeedbackCount(pending)
    } catch { void 0 }
    
  } catch (error) {
    console.error('  加载反馈列表失败:', error)
    ElMessage.error('加载反馈列表失败')
  } finally {
    loading.value = false
  }
}

// 解析错误字段字符串为changes数组
function parseChanges(errorFields) {
  if (!errorFields) return []
  const fields = errorFields.split(',').map(f => f.trim())
  return fields.map(f => {
    const key = getFieldKey(f)
    return {
      field: key,
      fieldLabel: getFieldLabel(key),
      requested: '',
      decision: 'accept'
    }
  })
}

// 获取字段中文名称
function getFieldLabel(field) {
  const labels = {
    name: '姓名',
    gender: '性别',
    birthDate: '出生日期',
    phone: '联系电话',
    email: '电子邮箱',
    title: '职称',
    status: '当前状态',
    hireDate: '入职时间',
    education: '最高学历',
    degree: '最高学位',
    department: '所属教研室',
    college: '所属学院',
    role: '角色'
  }
  return labels[field] || field
}

function getFieldKey(label) {
  const map = {
    姓名: 'name',
    性别: 'gender',
    出生日期: 'birthDate',
    联系电话: 'phone',
    电子邮箱: 'email',
    职称: 'title',
    当前状态: 'status',
    入职时间: 'hireDate',
    最高学历: 'education',
    最高学位: 'degree',
    所属教研室: 'department',
    所属学院: 'college',
    角色: 'role',
    工号: 'number'
  }
  return map[label] || label
}

async function openViewDialog(teacher) {
  if (!canViewTeacherStatus.value) return
  try {
    const id = teacher?.id
    if (!id) {
      selectedTeacher.value = teacher
      viewDialogOpen.value = true
      return
    }
    const res = await getTeacherById(id)
    const d = res?.data ?? res ?? {}
    const mapped = {
      id: d.id,
      number: d.employeeNumber || d.number || teacher.number || '',
      name: d.name || teacher.name || '',
      gender: toGenderLabel(d.genderName ?? d.gender ?? d.genderCode ?? teacher.gender),
      birthDate: d.birthDate || teacher.birthDate || '',
      title: d.professionalTitle || d.title || teacher.title || '',
      status: d.statusName || toStatusLabel(d.currentStatus) || (teacher.status || ''),
      hireDate: d.employmentDate || d.entryTime || teacher.hireDate || '',
      education: toEducationLabel(d.highestEducation ?? d.educationLevel ?? d.education ?? d.educationBackground ?? teacher.education),
      degree: toDegreeLabel(d.highestDegree ?? d.degreeName ?? d.degree ?? teacher.degree),
      college: d.college || teacher.college || '信息科学与工程学院',
      department: d.department || teacher.department || '',
      phone: d.phone || teacher.phone || '',
      email: d.email || teacher.email || '',
      role: d.role || teacher.role || '普通教师',
      remark: d.remark || teacher.remark || ''
    }
    selectedTeacher.value = mapped
    viewDialogOpen.value = true
  } catch {
    selectedTeacher.value = teacher
    viewDialogOpen.value = true
  }
}

async function openEditDialog(teacher) {
  if (!canModifyTeacherStatus.value) return
  try {
    const id = teacher?.id
    let d = teacher
    if (id) {
      const res = await getTeacherById(id)
      d = res?.data ?? res ?? teacher
    }
    const source = {
      id: d.id,
      number: d.employeeNumber || d.number || teacher.number || '',
      name: d.name || teacher.name || '',
      gender: toGenderLabel(d.genderName ?? d.gender ?? d.genderCode ?? teacher.gender),
      birthDate: d.birthDate || teacher.birthDate || '',
      title: d.professionalTitle || d.title || teacher.title || '',
      status: d.statusName || toStatusLabel(d.currentStatus) || (teacher.status || ''),
      hireDate: d.employmentDate || d.entryTime || teacher.hireDate || '',
      education: toEducationLabel(d.highestEducation ?? d.educationLevel ?? d.education ?? d.educationBackground ?? teacher.education),
      degree: toDegreeLabel(d.highestDegree ?? d.degreeName ?? d.degree ?? teacher.degree),
      college: d.college || teacher.college || '信息科学与工程学院',
      department: d.department || teacher.department || '',
      phone: d.phone || teacher.phone || '',
      email: d.email || teacher.email || '',
      role: d.role || teacher.role || '普通教师',
      remark: d.remark || teacher.remark || ''
    }
    selectedTeacher.value = source
    fillForm(editTeacherForm.value, source)
    editDialogOpen.value = true
  } catch {
    selectedTeacher.value = teacher
    fillForm(editTeacherForm.value, teacher)
    editDialogOpen.value = true
  }
}

async function openHandleDialog(report) {
  selectedReport.value = JSON.parse(JSON.stringify(report))
  let teacher = null
  const fromListById = teachers.value.find((t) => t.id === report.teacher.id)
  const fromListByNumber = teachers.value.find((t) => t.number === report.teacher.number)
  const candidate = fromListById || fromListByNumber || null
  let id = report.teacher?.id || candidate?.id || null
  try {
    if (!id && report.teacher?.name) {
      const res = await getTeacherList({ page: 1, pageSize: 1, name: report.teacher.name })
      const pageData = res?.data ?? res
      const rec = (pageData?.records || [])[0]
      id = rec?.id || id
    }
    if (id) {
      const res = await getTeacherById(id)
      const d = res?.data ?? res ?? {}
      teacher = {
        id: d.id,
        number: d.employeeNumber || d.number || candidate?.number || report.teacher.number || '',
        name: d.name || candidate?.name || report.teacher.name || '',
        gender: toGenderLabel(d.genderName ?? d.gender ?? d.genderCode ?? candidate?.gender),
        birthDate: d.birthDate || candidate?.birthDate || '',
        title: d.professionalTitle || d.title || candidate?.title || '',
        status: d.statusName || toStatusLabel(d.currentStatus) || candidate?.status || '',
        hireDate: d.employmentDate || d.entryTime || candidate?.hireDate || '',
        education: toEducationLabel(d.highestEducation ?? d.educationLevel ?? d.education ?? d.educationBackground ?? candidate?.education),
        degree: toDegreeLabel(d.highestDegree ?? d.degreeName ?? d.degree ?? candidate?.degree),
        college: d.college || candidate?.college || '',
        department: d.department || candidate?.department || '',
        phone: d.phone || candidate?.phone || '',
        email: d.email || candidate?.email || '',
        role: d.role || candidate?.role || '普通教师',
        remark: d.remark || candidate?.remark || ''
      }
      selectedReport.value.teacher.id = d.id
    } else if (candidate) {
      teacher = candidate
    }
  } catch { void 0 }

  fillForm(handleFormData.value, teacher || { ...report.teacher })
  handleEditableFields.value = (selectedReport.value?.changes || []).map((change) => change.field)
  selectedReport.value?.changes?.forEach((change) => {
    change.decision = change.decision || 'accept'
  })
  handleNote.value = ''
  handleDialogOpen.value = true
}

async function saveEditTeacher() {
  try {
    loading.value = true
    
    const payload = mapTeacherToApi(editTeacherForm.value)
    
    await updateTeacherInfo(payload)
    
    ElMessage.success('保存成功')
    editDialogOpen.value = false
    
    // 重新加载教师列表
    await fetchTeachers()
    
  } catch (error) {
    console.error('  保存教师信息失败:', error)
    ElMessage.error('保存失败')
  } finally {
    loading.value = false
  }
}

// 已移除 markReport，统一由 submitHandleResult 提交处理结果

async function submitHandleResult() {
  if (!selectedReport.value) return
  
  try {
    loading.value = true
    
    const teacherData = buildPartialUpdatePayload({ ...handleFormData.value, id: selectedReport.value.teacher.id }, handleEditableFields.value)
    if (Object.keys(teacherData).length > 1) {
      await updateTeacherInfo(teacherData)
    }
    
    // 步骤2：标记反馈为已处理
    await updateFeedbackStatus(selectedReport.value.id, 2)
    
    ElMessage.success('处理成功')
    handleDialogOpen.value = false
    
    // 重新加载数据
    await Promise.all([fetchTeachers(), fetchFeedbacks()])
    
  } catch (error) {
    console.error('  处理申请失败:', error)
    ElMessage.error('处理失败')
  } finally {
    loading.value = false
  }
}

async function submitIgnoreResult() {
  if (!selectedReport.value) return
  try {
    loading.value = true
    await updateFeedbackStatus(selectedReport.value.id, 3)
    ElMessage.success('已忽略该申请')
    handleDialogOpen.value = false
    await fetchFeedbacks()
  } catch (error) {
    console.error('  忽略失败:', error)
    ElMessage.error('忽略失败')
  } finally {
    loading.value = false
  }
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}/${m}/${d}`
}

function formatDateForInput(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDateTime(value) {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return value
  return `${formatDate(date)} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`
}

function calculateYearsOfService(date) {
  if (!date) return 0
  const start = new Date(date)
  const now = new Date()
  let years = now.getFullYear() - start.getFullYear()
  const monthDiff = now.getMonth() - start.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < start.getDate())) years -= 1
  return years
}

function statusClass(status) {
  switch (status) {
    case '在职':
      return 'active'
    case '休假':
      return 'leave'
    case '离职':
      return 'off'
    default:
      return 'off'
  }
}

function statusTagType(status) {
  if (!status) return 'info'
  switch (status) {
    case '在职':
      return 'success'
    case '休假':
      return 'warning'
    case '离职':
      return 'info'
    default:
      return 'info'
  }
}

// 初始化加载
onMounted(() => {
  fetchTeachers()
  fetchFeedbacks()
})

// 监听tab切换
watch(activeTab, (newTab) => {
  if (newTab === 'reports') {
    fetchFeedbacks()
  }
})

</script>

<style scoped>
.card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.tab-wrapper {
  background: #f3f4f6;
  padding: 6px;
  border-radius: 999px;
}

:deep(.capsule-tabs .el-tabs__header) {
  margin-bottom: 1rem;
}
:deep(.capsule-tabs .el-tabs__nav-wrap::after) {
  display: none;
}
:deep(.capsule-tabs .el-tabs__nav) {
  background-color: #f3f4f6;
  border-radius: 1rem;
  padding: 4px;
  border: none;
  width: 100%;
}
:deep(.capsule-tabs .el-tabs__active-bar) {
  display: none;
}
:deep(.capsule-tabs .el-tabs__item) {
  padding: 0;
  border: none;
  flex: 1;
}
.tab-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 36px;
  padding: 0 16px;
  border-radius: 12px;
  color: #374151;
  transition: all 0.2s;
}
:deep(.capsule-tabs .el-tabs__item.is-active .tab-chip) {
  background-color: #fff;
  color: #111827;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.badge-red {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: #ef4444;
  border-radius: 10px;
  margin-left: 6px;
}

.filter-panel {
  background: #fff;
  border-radius: 24px;
}

.filter-input :deep(.el-input__wrapper) {
  border-radius: 12px;
  background: #f5f6fb;
  border: 1px solid transparent;
  box-shadow: none;
  height: 44px;
  width: 100%;
}

.filter-field span {
  font-size: 14px;
  color: #6b7280;
  margin-right: 6px;
}
.filter-field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.select-field :deep(.el-input__wrapper) {
  border-radius: 12px;
  background: #f5f6fb;
  border: none;
  box-shadow: none;
}

.plain-popper .el-select-dropdown__item {
  border-radius: 6px;
}

.teacher-card {
  padding: 18px;
  border-radius: 22px;
}
.info-line {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 8px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  font-size: 12px;
  border-radius: 999px;
  font-weight: 500;
  background: #f3f4f6;
  color: #374151;
}
.status-chip.active { background: #ecfdf5; color: #0f5132; }
.status-chip.leave { background: #fff7ed; color: #92400e; }
.status-chip.off { background: #f5f5f5; color: #6b7280; }
.status-chip.pending { background: #fdf2f8; color: #c026d3; }
.status-chip.processed { background: #ecfdf5; color: #0f5132; }
.status-chip.ignored { background: #ffffff; color: #6b7280; border: 1px solid #e5e7eb; }

.title-chip {
  padding: 2px 10px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 12px;
}

.ghost-btn,
.primary-btn,
.muted-btn,
.success-btn {
  border-radius: 10px;
  padding: 0 16px;
  height: 36px;
  font-size: 14px;
  font-weight: 500;
}

.ghost-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
}
.ghost-btn:hover { background: #f3f4f6; }

.outline-btn {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 12px;
  padding: 0 18px;
  height: 38px;
  font-size: 14px;
  color: #111827;
}
.outline-btn:hover {
  background: #f3f4f6;
}

.primary-btn {
  border: none;
  background: #274bdb;
  color: #fff;
  box-shadow: 0 12px 30px rgba(39, 75, 219, 0.25);
}
.primary-btn:hover { background: #1f3fc5; }

.muted-btn {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
}

.success-btn {
  border: none;
  background: #16a34a;
  color: #fff;
  box-shadow: 0 12px 25px rgba(22, 163, 74, 0.2);
}

.pagination {
  display: flex;
  gap: 8px;
}
.pagination button {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 0 14px;
  height: 36px;
  font-size: 14px;
}
.pagination button.active {
  background: #111827;
  color: #fff;
  border-color: #111827;
}
.pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.report-card {
  padding: 18px;
}

.feedback-card {
  border-left: 4px solid #2563eb;
  background: #eef2ff;
  padding: 14px 16px;
  border-radius: 12px;
  color: #1f2937;
}
.feedback-card .label {
  font-size: 12px;
  color: #2563eb;
  font-weight: 600;
  margin-right: 6px;
}

.history-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f9fafb;
}

.teacher-dialog :deep(.el-dialog__body) {
  padding-top: 6px;
}

.detail-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.detail-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.detail-cell {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
}
.detail-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 2px;
}
.detail-value {
  font-size: 14px;
  color: #111827;
}

.edit-form .form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.edit-form :deep(.el-input__wrapper),
.edit-form :deep(.el-select .el-input__wrapper),
.edit-form :deep(.el-date-editor .el-input__wrapper) {
  border-radius: 12px;
  background: #f5f6fb;
  border: 1px solid transparent;
  box-shadow: none;
}
.edit-form :deep(.el-input__wrapper:hover),
.edit-form :deep(.el-input__wrapper.is-focus) {
  background: #fff;
  border-color: #d1d5db;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.08);
}
.input-muted :deep(.el-input__wrapper) {
  background: #f3f4f6;
  color: #6b7280;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 对话框关闭按钮样式 - 纯黑色 */
:deep(.el-dialog__headerbtn) {
  transition: all 0.2s;
}
:deep(.el-dialog__headerbtn:hover .el-dialog__close) {
  color: #000 !important;
}

/* 取消按钮样式 - 灰色不变 */
:deep(.el-button:not(.el-button--primary)) {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}
:deep(.el-button:not(.el-button--primary):hover) {
  background-color: #e5e7eb;
  border-color: #9ca3af;
  color: #1f2937;
}

/* 保存修改按钮样式 - 与 React 一致 */
:deep(.el-button--primary) {
  background-color: #2975bc;
  border-color: #2975bc;
  color: #fff;
}
:deep(.el-button--primary:hover) {
  background-color: #0855ad;
  border-color: #0855ad;
  color: #fff;
}

/* 确认处理按钮 - 绿色 */
.success-button {
  background-color: #16a34a !important;
  border-color: #16a34a !important;
  color: #fff !important;
}
.success-button:hover {
  background-color: #15803d !important;
  border-color: #15803d !important;
  color: #fff !important;
}

/* 编辑对话框中的输入框样式 - 与 React 一致 */
:deep(.el-dialog .el-input__wrapper),
:deep(.el-input__wrapper) {
  background-color: #f3f3f5 !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: none !important;
  transition: all 0.2s;
}
:deep(.el-dialog .el-input__wrapper:hover),
:deep(.el-input__wrapper:hover) {
  border-color: rgba(0, 0, 0, 0.2) !important;
  box-shadow: none !important;
}
:deep(.el-dialog .el-input__wrapper.is-focus),
:deep(.el-input__wrapper.is-focus),
:deep(.el-dialog .el-input:focus .el-input__wrapper),
:deep(.el-input:focus .el-input__wrapper),
:deep(.el-dialog .el-input__inner:focus),
:deep(.el-input__inner:focus) {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
  outline: none !important;
}
:deep(.el-input.is-disabled .el-input__wrapper) {
  background-color: #f9fafb !important;
}

/* 移除所有蓝色边框和阴影 */
:deep(.el-input__wrapper.is-focus),
:deep(.el-textarea__inner:focus) {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 下拉框选项样式 - 移除蓝色，统一灰色 */
.el-select-dropdown__item.is-selected {
  color: #1f2937 !important;
  background-color: #f3f4f6 !important;
  font-weight: 500 !important;
}
.el-select-dropdown__item.is-hovering {
  background-color: #e5e7eb !important;
  color: #111827 !important;
}
.el-select-dropdown__item {
  color: #374151 !important;
}
.el-popper.is-light {
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  background-color: #ffffff !important;
}

/* 日期选择器样式 */
:deep(.el-date-editor .el-input__wrapper) {
  background-color: #f3f3f5 !important;
}
:deep(.el-picker-panel) {
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}
:deep(.el-date-table td.current:not(.disabled) .el-date-table-cell__text) {
  background-color: #1976d2 !important;
}
:deep(.el-date-table td.today .el-date-table-cell__text) {
  color: #1976d2 !important;
}
</style>

<style>
/* 全局样式 - 移除所有蓝色边框 */
.el-input__wrapper.is-focus {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}

.el-input__wrapper:focus,
.el-input__wrapper:focus-within {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}

.el-textarea__inner:focus {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
  outline: none !important;
}

.el-select .el-input.is-focus .el-input__wrapper {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}

.el-date-editor.el-input__wrapper.is-focus {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}

/* 强制覆盖 Element Plus 默认的蓝色样式 */
.el-select:not(.is-disabled) .el-input__wrapper.is-focus {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}

.el-input:not(.is-disabled) .el-input__wrapper.is-focus {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}

/* 移除下拉框打开时的蓝色边框 */
.el-select .el-input.is-focus .el-input__wrapper,
.el-select.is-focused .el-input__wrapper,
.el-select--default.is-focused .el-input__wrapper {
  border-color: rgba(0, 0, 0, 0.3) !important;
  box-shadow: none !important;
}
</style>
.teacher-dialog :deep(.el-date-editor) {
  width: 100% !important;
}
